/**
 * #40 · Cadena de custodia del rollo. **Espejo de
 * `services/api/schemas/labels.py`** (`RollState` y `ROLL_TRANSITIONS`).
 *
 * Para qué sirve tener la máquina de estados también acá
 * -----------------------------------------------------
 * Para ofrecer **sólo los destinos legítimos** en el desplegable de "mover el
 * rollo". La alternativa —listar los nueve estados y dejar que la API rechace
 * ocho— convierte una máquina de estados en una lotería de 409, y en una
 * pantalla de cadena de custodia eso enseña a la gente a ignorar los errores.
 *
 * La autoridad sigue siendo la API: valida la transición del rollo Y la de la
 * unidad (`shared/states.py`) antes de tocar nada. Si estas dos tablas
 * divergen, lo peor que pasa es que el dashboard ofrezca de menos.
 *
 * Nada apunta hacia atrás, a propósito: la cadena de custodia es un registro
 * de lo que pasó. "Deshacer" un envío no es un estado, es una corrección, y
 * tiene que quedar como evento propio.
 */
import type { MessageKey } from '@/i18n/messages';
import type { Tone } from '@/ui/Badge';

export const ROLL_STATES = [
  'ordered',
  'printed',
  'shipped',
  'received',
  'in_use',
  'consumed',
  'stolen',
  'destroyed',
  'voided',
] as const;

export type RollState = (typeof ROLL_STATES)[number];

export function isRollState(value: string): value is RollState {
  return (ROLL_STATES as readonly string[]).includes(value);
}

/** La cadena feliz, en orden. Es lo que se dibuja como progreso en el detalle. */
export const CUSTODY_CHAIN: readonly RollState[] = [
  'ordered',
  'printed',
  'shipped',
  'received',
  'in_use',
  'consumed',
];

/** Salidas de emergencia mientras el rollo esté en circulación. */
const EXITS: readonly RollState[] = ['stolen', 'destroyed', 'voided'];

export const ROLL_TRANSITIONS: Record<RollState, readonly RollState[]> = {
  ordered: ['printed', ...EXITS],
  printed: ['shipped', ...EXITS],
  shipped: ['received', ...EXITS],
  received: ['in_use', ...EXITS],
  in_use: ['consumed', ...EXITS],
  // Un rollo consumido ya no tiene etiquetas que robar; lo único que le queda
  // es la destrucción documentada del soporte.
  consumed: ['destroyed'],
  stolen: [],
  destroyed: [],
  voided: [],
};

export function rollCanTransition(current: RollState, target: RollState): boolean {
  return ROLL_TRANSITIONS[current].includes(target);
}

/**
 * Destinos que se ofrecen en el formulario de transición.
 *
 * `stolen` se saca: tiene endpoint propio (`POST /rolls/{id}/stolen`) porque
 * exige un relato de lo que pasó y rol `admin`, y la API rechaza con
 * `use_stolen_endpoint` si entra por acá.
 */
export function transitionTargets(current: RollState): readonly RollState[] {
  return ROLL_TRANSITIONS[current].filter((state) => state !== 'stolen');
}

export const ROLL_STATE_LABEL_KEYS: Record<RollState, MessageKey> = {
  ordered: 'labels.roll.state.ordered',
  printed: 'labels.roll.state.printed',
  shipped: 'labels.roll.state.shipped',
  received: 'labels.roll.state.received',
  in_use: 'labels.roll.state.in_use',
  consumed: 'labels.roll.state.consumed',
  stolen: 'labels.roll.state.stolen',
  destroyed: 'labels.roll.state.destroyed',
  voided: 'labels.roll.state.voided',
};

/**
 * `stolen` en rojo y nada más. Un rollo robado es la regla crítica
 * `stolen_roll`: cada escaneo de sus etiquetas genera alerta y alguien tiene
 * que estar mirando. `destroyed` y `voided` son finales pero ordenados: son
 * estado, no emergencia.
 */
export const ROLL_STATE_TONES: Record<RollState, Tone> = {
  ordered: 'progress',
  printed: 'progress',
  shipped: 'progress',
  received: 'verified',
  in_use: 'verified',
  consumed: 'neutral',
  stolen: 'critical',
  destroyed: 'neutral',
  voided: 'suspect',
};

// ─────────────────────────────────────────────── órdenes de etiquetas

export const ORDER_STATUSES = ['draft', 'generating', 'generated', 'cancelled'] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

export function isOrderStatus(value: string): value is OrderStatus {
  return (ORDER_STATUSES as readonly string[]).includes(value);
}

export const ORDER_STATUS_LABEL_KEYS: Record<OrderStatus, MessageKey> = {
  draft: 'labels.order.status.draft',
  generating: 'labels.order.status.generating',
  generated: 'labels.order.status.generated',
  cancelled: 'labels.order.status.cancelled',
};

export const ORDER_STATUS_TONES: Record<OrderStatus, Tone> = {
  draft: 'neutral',
  generating: 'progress',
  generated: 'verified',
  cancelled: 'neutral',
};

/**
 * Rango de series de un rollo o de una orden, ya formateado.
 *
 * Es lo que pide el criterio de aceptación de #40 y es la identidad operativa
 * del rollo frente al impresor (§5 de la especificación al proveedor: cada
 * rollo lleva un rango contiguo y los rangos nunca se solapan).
 *
 * Ojo con lo que esto NO es: el rango son dos **bordes**, no la identidad de
 * una unidad. En ninguna vista de estos módulos se renderiza el `unit_id` de
 * una unidad concreta — para eso está el código público. Ver la ADR
 * `docs/adr-inbox/b6-catalogo.md`.
 */
export function serialRange(
  from: number | null | undefined,
  to: number | null | undefined,
  format: (value: number) => string,
): string | null {
  if (from === null || from === undefined || to === null || to === undefined) return null;
  return `${format(from)} – ${format(to)}`;
}
